import { Fragment } from 'react';
import StudentDetailExpandedView from '../StudentList/StudentDetailExpandedView';
import StudentDetailInlineEdit from '../StudentList/StudentDetailInlineEdit';
import EnrollmentStatusControl from '../StudentList/EnrollmentStatusControl';
import HighestDegreeYearCell from './HighestDegreeYearCell';
import { DIRECTORY_COLUMNS } from '../../domain/filters/directoryColumns';
import { hasLeftEnrollment } from '../../utils/studentEnrollmentStatus';
import { BULK_DELETE_LIMIT } from '../../utils/bulkStudentSelection';

function displayName(s) {
  const name =
    s.fullName ||
    s.full_name ||
    `${s.firstName || ''} ${s.lastName || ''}`.trim() ||
    s.id ||
    'N/A';
  if (s.isStaging) {
    return `${name} [${s.stagingStatus || 'staging'}]`;
  }
  return name;
}

function displayEmail(s) {
  return s.emailAddress || s.email_address || s.email || 'N/A';
}

function isStudentRow(s) {
  return (!s.userType || s.userType === 'student') && !s.isStaging;
}

/**
 * Zoho-style results table. Same actions as StudentList; columns come from directoryColumns.
 */
const StudentDirectoryTable = ({
  students,
  expandedId,
  toggleExpand,
  expandedStudentDetails = {},
  expandedDetailLoadingId = null,
  onDeleteStudent,
  onToggleSelection,
  selectedStudents = [],
  onBulkDelete,
  showBulkActions = false,
  selectionLimit = BULK_DELETE_LIMIT,
  selectionAtLimit = false,
  selectionWarning = '',
  showStatusControls = false,
  onEnrollmentStatusChange,
  onBulkMarkAsLeft,
  statusUpdatingIds = new Set(),
  bulkStatusUpdating = false,
  showInlineEdit = false,
  canEditStudents = true,
  canDeleteStudents = true,
  editingStudentId = null,
  onStartEdit,
  onCancelEdit,
  onSaveInlineEdit,
  inlineEditSavingId = null,
  inlineEditError = null,
  inlineEditToken = null,
  onOpenFullEdit,
  onChangeBatch,
  onChangePassword,
  onHighestDegreeYearPatched,
}) => {
  if (students.length === 0) return <p>No users match the filter.</p>;

  const allowInlineEdit = showInlineEdit && canEditStudents;
  const allowDelete = Boolean(onDeleteStudent) && canDeleteStudents;
  const allowBulkActions = showBulkActions && (canDeleteStudents || (showStatusControls && canEditStudents));
  const allowStatusControls = showStatusControls && canEditStudents;

  return (
    <div>
      <p className="text-sm text-slate-600 mb-3">
        Showing {students.length} user{students.length !== 1 ? 's' : ''}
        {students.some((s) => s.isStaging)
          ? ` (incl. ${students.filter((s) => s.isStaging).length} staging)`
          : ''}
      </p>
      {selectionWarning && allowBulkActions ? (
        <p className="text-sm text-amber-900 bg-amber-100 border border-amber-300 rounded-md px-3 py-2 mb-3">
          {selectionWarning}
        </p>
      ) : null}
      {allowBulkActions && selectedStudents.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 rounded-lg mb-3">
          <span className="text-sm font-medium text-blue-800">
            {selectedStudents.length} / {selectionLimit} selected
          </span>
          {allowStatusControls && onBulkMarkAsLeft ? (
            <button
              type="button"
              onClick={onBulkMarkAsLeft}
              disabled={bulkStatusUpdating}
              className="px-3 py-1.5 bg-amber-600 text-white rounded text-sm disabled:opacity-50"
            >
              {bulkStatusUpdating ? 'Updating…' : 'Mark as Left'}
            </button>
          ) : null}
          {allowDelete && onBulkDelete ? (
            <button
              type="button"
              onClick={onBulkDelete}
              className="px-3 py-1.5 bg-red-600 text-white rounded text-sm"
            >
              Delete Selected
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
              {allowBulkActions ? <th className="p-2 w-8"> </th> : null}
              {DIRECTORY_COLUMNS.map((col) => (
                <th key={col.id} className="p-2 font-semibold">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {students.map((s) => {
              const rowId = s.id || s._id;
              const rowSelected = selectedStudents.some((selected) => selected.id === s.id);
              const rowCheckboxDisabled = showBulkActions && selectionAtLimit && !rowSelected;
              const isLeftStudent = hasLeftEnrollment(s);
              const expanded = expandedId === rowId;

              return (
                <Fragment key={rowId}>
                <tr className={isLeftStudent ? 'bg-amber-50' : 'hover:bg-slate-50'}>
                  {allowBulkActions ? (
                    <td className="p-2 align-top">
                      <input
                        type="checkbox"
                        checked={rowSelected}
                        disabled={rowCheckboxDisabled || s.isStaging}
                        onChange={() => onToggleSelection(s)}
                      />
                    </td>
                  ) : null}
                  <td className="p-2 align-top">
                    <div className="font-semibold text-slate-900">
                      {displayName(s)}
                      {s.isStaging ? (
                        <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-900">
                          staging
                        </span>
                      ) : null}
                      {s.userType && s.userType !== 'student' ? (
                        <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800">
                          {s.userType}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-600">{displayEmail(s)}</div>
                  </td>
                  <td className="p-2 align-top">
                    <HighestDegreeYearCell
                      student={s}
                      canEdit={canEditStudents && isStudentRow(s)}
                      onPatched={onHighestDegreeYearPatched}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <button
                      type="button"
                      onClick={() => toggleExpand(rowId)}
                      className="text-blue-700 hover:underline text-sm"
                    >
                      {expanded ? 'Hide details' : 'Complete details'}
                    </button>
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {onChangeBatch && isStudentRow(s) ? (
                        <button
                          type="button"
                          onClick={() => onChangeBatch(s)}
                          disabled={!s.enrollments?.length}
                          className="text-green-700 text-xs px-1.5 py-0.5 rounded hover:bg-green-50 disabled:opacity-40"
                        >
                          Change batch
                        </button>
                      ) : null}
                      {onChangePassword ? (
                        <button
                          type="button"
                          onClick={() => onChangePassword(s)}
                          className="text-slate-700 text-xs px-1.5 py-0.5 rounded hover:bg-slate-100"
                        >
                          Password
                        </button>
                      ) : null}
                      {allowStatusControls && onEnrollmentStatusChange && isStudentRow(s) ? (
                        <EnrollmentStatusControl
                          student={s}
                          onStatusChange={onEnrollmentStatusChange}
                          updating={statusUpdatingIds.has(rowId)}
                        />
                      ) : null}
                      {allowInlineEdit && isStudentRow(s) && onOpenFullEdit ? (
                        <button
                          type="button"
                          onClick={() => onOpenFullEdit(s)}
                          className="text-indigo-700 text-xs px-1.5 py-0.5 rounded hover:bg-indigo-50"
                        >
                          Full edit
                        </button>
                      ) : null}
                      {allowInlineEdit && isStudentRow(s) && onStartEdit ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (editingStudentId === rowId) onCancelEdit?.();
                            else {
                              onStartEdit(rowId);
                              if (expandedId !== rowId) toggleExpand(rowId);
                            }
                          }}
                          className="text-blue-700 text-xs px-1.5 py-0.5 rounded hover:bg-blue-50"
                        >
                          {editingStudentId === rowId ? 'Cancel edit' : 'Edit'}
                        </button>
                      ) : null}
                      {allowDelete && isStudentRow(s) ? (
                        <button
                          type="button"
                          onClick={() => onDeleteStudent(s)}
                          className="text-red-700 text-xs px-1.5 py-0.5 rounded hover:bg-red-50"
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
                {expanded ? (
                  <tr key={`${rowId}-details`} className="bg-white">
                    <td
                      colSpan={DIRECTORY_COLUMNS.length + (allowBulkActions ? 1 : 0)}
                      className="p-4 border-t"
                    >
                      {expandedDetailLoadingId === rowId ? (
                        <p className="text-sm text-slate-500">Loading full student details…</p>
                      ) : allowInlineEdit && editingStudentId === rowId && onSaveInlineEdit ? (
                        <StudentDetailInlineEdit
                          student={expandedStudentDetails[rowId] || s}
                          token={inlineEditToken}
                          saving={inlineEditSavingId === rowId}
                          error={editingStudentId === rowId ? inlineEditError : null}
                          onCancel={() => onCancelEdit?.()}
                          onSave={(formData, helpers) =>
                            onSaveInlineEdit(expandedStudentDetails[rowId] || s, formData, helpers)
                          }
                        />
                      ) : (
                        <StudentDetailExpandedView
                          student={expandedStudentDetails[rowId] || s}
                        />
                      )}
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StudentDirectoryTable;
